import React from 'react';
import { Button, message } from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../store/AuthContext';
import { PERMS } from '../../constants/permissions';
import {
    exportTableToExcel,
    exportStamp,
    ExcelColumn,
    ExcelCellValue,
} from '../../utils/excelExport';

type ExcelExportButtonProps<T> = {
    /** Base file name; a timestamp is appended so exports never overwrite. */
    fileName: string;
    sheetName?: string;
    columns: ExcelColumn<T>[];
    rows: T[];
    /** Bold rows appended below the table — totals, grand totals, and so on. */
    footer?: ExcelCellValue[][];
    /** Lines written above the header, e.g. the report title and period. */
    title?: string[];
    disabled?: boolean;
    block?: boolean;
};

/**
 * The one Excel download button used by every report screen, so all reports
 * export the same way and are gated by the same permission.
 */
function ExcelExportButton<T>({
    fileName,
    sheetName,
    columns,
    rows,
    footer,
    title,
    disabled,
    block,
}: ExcelExportButtonProps<T>) {
    const { t } = useTranslation();
    const { hasAnyPermission } = useAuth();

    // Reports are readable by more roles than may export them.
    const canExport = hasAnyPermission(
        PERMS.exportData.excel,
        PERMS.exportData.report,
        PERMS.report.export
    );
    if (!canExport) return null;

    const handleClick = () => {
        try {
            exportTableToExcel<T>({
                fileName: `${fileName}-${exportStamp()}`,
                sheetName: sheetName || fileName,
                columns,
                rows,
                footer,
                title,
            });
        } catch (error: any) {
            message.error(error?.message || t('export_failed') || 'Export failed');
        }
    };

    return (
        <Button
            icon={<FileExcelOutlined />}
            onClick={handleClick}
            disabled={disabled || rows.length === 0}
            block={block}
            style={{
                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                borderColor: '#15803d',
                color: '#fff',
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(22,163,74,0.35)',
            }}
        >
            {t('download_excel') || 'Download Excel'}
        </Button>
    );
}

export default ExcelExportButton;
